<template>
  <div id="auth-container">
    <div class="mb-5" v-if="!isCreditCheckPermitted">
      <div class="mt-3 auth-provided">
        <i class="fa fa-check" aria-hidden="true"></i>
        {{ t("AUTH_PROVIDED") }}
      </div>
    </div>
    <div v-if="isCreditCheckPermitted">
      <ToggleButton
        :left-label="t('TYPE')"
        :right-label="t('DRAW')"
        @update-switch="handleTypeDrawOptions"
      />
      <TextSignature
        v-if="!isDrawActive"
        :label="t('CUSTOMER_SIGNATURE')"
        v-model.trim="textName"
        @clear="handleClearESign"
        @e-sign="handleESign"
        :key="componentKey"
      />
      <DrawSignature
        v-if="isDrawActive"
        :label="t('CUSTOMER_SIGNATURE')"
        @clear="handleClearESign"
        @e-sign="handleESign"
      />
      <div class="toggle-text text-center">
        <span
          >{{ t("E_SIGN.UNABLE") }}
          <button class="auth-link" @click="handleSwitchToSMS">
            {{ t("SMS.GET_PIN") }}
          </button>
        </span>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { useI18n } from "@/apps/merchant3/composables/useI18n";
import { useStore } from "vuex";
import { computed, onMounted, ref, watch } from "vue";
import { AuthType } from "@/apps/merchant3/types/CreditCheckPermission";
import DrawSignature from "@/apps/merchant3/shared-components/common/DrawSignature.vue";
import TextSignature from "@/apps/merchant3/shared-components/common/TextSignature.vue";
import ToggleButton from "@/apps/merchant3/shared-components/common/ToggleButton.vue";
import logEvent from "@/apps/merchant3/services/event-logs.service";
import EventLogs from "@/apps/merchant3/types/EventLogs";

enum Signature {
  Type = 1,
  Draw = 2,
}

const emit = defineEmits(["changeSignature", "switchAuthMethod"]);
const { t } = useI18n();
const store = useStore();

const isDrawActive = ref(false);
const customerName = computed(() => store.state.customer?.name || "");
const textName = ref("");

onMounted(() => {
  autoFillSignature();
});

const autoFillSignature = () => {
  if (customerName.value != "") {
    textName.value = customerName.value;
    logEvent(EventLogs.Events.CREDIT_CHECK_PERMISSION_ESIGN_AUTO_FILLED);
    forceRerender();
  }
};

const isCreditCheckPermitted = computed(
  () => !store.state.carts?.creditCheckPermittedTimestamp
);

const handleClearESign = () => {
  textName.value = "";
  logEvent(EventLogs.Events.CREDIT_CHECK_PERMISSION_CLEARED_ESIGN);
  emit("changeSignature", { signature: "", enableContinue: false });
};

const handleESign = (signature) => {
  const canContinue = !!signature;
  if (canContinue) {
    if (isDrawActive.value) {
      logEvent(EventLogs.Events.CREDIT_CHECK_PERMISSION_ESIGN_DRAWN);
    }
  }

  emit("changeSignature", {
    signature: signature,
    enableContinue: canContinue,
  });
};

const handleTypeDrawOptions = (selectedOption: number) => {
  handleClearESign();
  isDrawActive.value = selectedOption === Signature.Draw;
};

const handleSwitchToSMS = () => {
  handleClearESign();
  emit("switchAuthMethod", AuthType.SMS);
};

const componentKey = ref(0);
const forceRerender = () => {
  componentKey.value += 1;
};
logEvent(EventLogs.Events.ESIGN_V2_LOADED);

watch(customerName, () => {
  if (!textName.value && !isDrawActive.value) {
    autoFillSignature();
  }
});
</script>
<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";

.auth-link {
  color: $pj-color-green;
  font-weight: 600;
  clear: both;
  text-decoration: none;
  font-size: 16px;
  background: none;
  border: none;
}
.auth-link:hover {
  font-weight: 700;
}

.toggle-text {
  margin: 14px 0 30px 0;
  font-size: 16px;
}

.auth-provided {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  color: $pj-color-green;
  font-weight: 600;
  font-size: 24px;
}
</style>
